import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Coins, Clock, ShieldCheck, UserCircle, FileText } from "@phosphor-icons/react";

const TYPE_META = {
  task_creation: { label: "New task review", badge: "Awaiting your approval", tone: "amber" },
  pickup_manager: { label: "Pick-up · manager sign-off", badge: "Awaiting manager", tone: "blue" },
  pickup_creator: { label: "Pick-up · creator sign-off", badge: "Awaiting creator approval", tone: "violet" },
  assignment: { label: "Assignment", badge: "Pending", tone: "amber" },
  completion: { label: "Completion", badge: "Pending", tone: "amber" },
  high_value: { label: "High value", badge: "Pending", tone: "amber" },
};

const TONE = {
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300",
  violet: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
};

export default function Approvals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/approvals/pending");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    try {
      await api.post(`/approvals/${id}/decide`, { decision, notes });
      toast.success(`Request ${decision}`);
      setNotes("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="approvals-page">
      <div className="mb-6 flex items-center gap-3">
        <ShieldCheck size={24} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Approvals</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold">Pending decisions</h1>
          <p className="text-sm text-zinc-500 mt-1">Requests routed to you for sign-off.</p>
        </div>
      </div>

      <div className="space-y-3">
        {!loading && items.length === 0 && (
          <div className="border border-dashed border-border p-12 text-center text-sm text-zinc-500" data-testid="approvals-empty">
            Inbox zero. No approvals pending for you.
          </div>
        )}
        {items.map((i) => {
          const meta = TYPE_META[i.type] || { label: i.type, badge: "Pending", tone: "amber" };
          const who = i.candidate?.name || i.requester?.name;
          return (
            <div key={i.approval_id} className="border border-border bg-background p-4" data-testid={`approval-${i.approval_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{meta.label}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${TONE[meta.tone]}`} data-testid={`approval-badge-${i.approval_id}`}>
                      {meta.badge}
                    </span>
                  </div>
                  <button onClick={() => navigate(`/tasks/${i.task_id}`)} className="text-lg font-semibold hover:underline underline-offset-4 mt-1 text-left block" data-testid={`approval-task-${i.approval_id}`}>
                    {i.task?.title || "(task removed)"}
                  </button>
                  <div className="flex items-center gap-1.5 text-sm text-zinc-500 mt-1">
                    {i.type.startsWith("pickup") ? <UserCircle size={14} /> : <FileText size={14} />}
                    <span>
                      {i.type === "task_creation"
                        ? <>Submitted by <span className="text-foreground">{i.requester?.name}</span></>
                        : <><span className="text-foreground">{who}</span> wants to pick up this task</>}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">{i.reason}</div>
                </div>
                <div className="text-right space-y-1 shrink-0">
                  {i.task && (
                    <>
                      <div className="flex items-center justify-end gap-1 text-xs"><Clock size={12} /><span>{i.task.effort_hours}h</span></div>
                      <div className="flex items-center justify-end gap-1 text-xs font-semibold text-[#E1252A]"><Coins size={12} weight="fill" /><span className="font-mono">{i.task.credit_cost}</span></div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-border">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-sm" data-testid={`reject-${i.approval_id}`}>Reject</Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-sm">
                    <DialogHeader><DialogTitle>Reject this request?</DialogTitle></DialogHeader>
                    <Textarea placeholder="Reason (optional)…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-sm" data-testid={`reject-notes-${i.approval_id}`} />
                    <DialogFooter>
                      <Button onClick={() => decide(i.approval_id, "rejected")} className="rounded-sm bg-foreground text-background" data-testid={`confirm-reject-${i.approval_id}`}>Confirm reject</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button size="sm" onClick={() => decide(i.approval_id, "approved")} className="rounded-sm bg-foreground text-background" data-testid={`approve-${i.approval_id}`}>Approve</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
